import React, { useEffect, useState } from 'react';
import { Mosque, MOSQUE_TYPES, MosquePhoto } from '../types/mosque';
import { useLanguage } from '../contexts/LanguageContext';
import { openDirectionsFromCurrentLocation } from '../utils/directions';
import { useModalAnimation } from '../hooks/useModalAnimation';
import { useSwipeToClose } from '../hooks/useSwipeToClose';
import { usePhotos } from '../hooks/usePhotos';
import { trackEvent } from '../lib/analytics';
import { useToast } from '../contexts/ToastContext';
import PhotoGallery from './PhotoGallery';
import AddPhotoForm from './AddPhotoForm';
import { Globe, Lock, MapPin, Pencil, Navigation, Check, X, HelpCircle, Images, Copy, Share2 } from 'lucide-react';

interface MosqueDetailsProps {
  mosque: Mosque | null;
  isOpen: boolean;
  onClose: () => void;
  onSuggestEdit?: (mosque: Mosque) => void;
}

const MosqueDetails: React.FC<MosqueDetailsProps> = ({ mosque, isOpen, onClose, onSuggestEdit }) => {
  const { t } = useLanguage();
  const toast = useToast();
  const { fetchApprovedPhotosForMosque } = usePhotos();
  const { isVisible, isClosing, handleClose } = useModalAnimation({ isOpen, onClose, animationDuration: 250 });
  const { handlers, style, overlayStyle } = useSwipeToClose({ onClose: handleClose, threshold: 80 });
  const [photos, setPhotos] = useState<MosquePhoto[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [isAddPhotoOpen, setIsAddPhotoOpen] = useState(false);
  const normalizedSourceLink = (() => {
    const raw = mosque?.sourceLink?.trim();
    if (!raw) return null;
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    return `https://${raw}`;
  })();

  const loadPhotos = async () => {
    if (!mosque) return;
    try {
      setIsLoadingPhotos(true);
      const approvedPhotos = await fetchApprovedPhotosForMosque(mosque.id);
      setPhotos(approvedPhotos);
    } catch (error) {
      console.error('Failed to fetch mosque photos:', error);
      setPhotos([]);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  useEffect(() => {
    if (isOpen && mosque) {
      loadPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mosque?.id]);

  const handleGetDirections = () => {
    if (!mosque) return;
    trackEvent('directions_clicked', {
      mosque_type: mosque.type,
      has_address: Boolean(mosque.location.address),
    });
    openDirectionsFromCurrentLocation(
      { lat: mosque.location.lat, lng: mosque.location.lng },
      mosque.name,
      (error) => {
        console.warn('Location error:', error);
      }
    );
  };

  const getFacilityStatus = (value: boolean | null) => {
    if (value === null) return { icon: <HelpCircle size={14} />, className: 'unknown' };
    if (value === true) return { icon: <Check size={14} />, className: 'available' };
    return { icon: <X size={14} />, className: 'not-available' };
  };

  const handleCoordinatesClick = async () => {
    if (!mosque) {
      return;
    }

    const coordinateText = `${mosque.location.lat.toFixed(6)}, ${mosque.location.lng.toFixed(6)}`;
    const copied = await copyToClipboard(coordinateText);
    if (copied) {
      trackEvent('coordinates_copied');
      toast.success(t('mosqueDetails.coordinatesCopied'));
      return;
    }

    toast.error(t('mosqueDetails.coordinatesCopyFailed'));
  };

  const handleAddressClick = async () => {
    const addressText = mosque?.location.address?.trim();
    if (!addressText) return;

    const copied = await copyToClipboard(addressText);
    if (copied) {
      trackEvent('address_copied');
      toast.success(t('mosqueDetails.addressCopied'));
      return;
    }

    toast.error(t('mosqueDetails.addressCopyFailed'));
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        console.error('Clipboard API write failed:', error);
      }
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    } catch (error) {
      console.error('execCommand copy failed:', error);
      return false;
    }
  };

  const handleShare = async () => {
    if (!mosque) return;

    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: mosque.name,
          text: `${mosque.name} - ${t('mosqueDetails.shareDescription')}`,
          url: shareUrl,
        });
        trackEvent('mosque_shared', {
          mosque_type: mosque.type,
          method: 'native',
        });
        return;
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return;
        }
        console.warn('Native share failed, falling back to copy:', error);
      }
    }

    const copied = await copyToClipboard(shareUrl);
    if (copied) {
      trackEvent('mosque_shared', {
        mosque_type: mosque.type,
        method: 'copy_link',
      });
      toast.success(t('mosqueDetails.linkCopied'));
      return;
    }

    toast.error(t('mosqueDetails.shareFailed'));
  };

  if (!isVisible || !mosque) return null;

  return (
    <div className={`details-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose} style={overlayStyle}>
      <div className="details-sheet" data-testid="mosque-details-sheet" onClick={(e) => e.stopPropagation()} style={style}>
        <div {...handlers}>
          <div className="drag-handle" />
          <div className="details-header">
            <div className="mosque-title">
              <h2>{mosque.name}</h2>
              <div
                className="mosque-type-badge"
                style={{ backgroundColor: MOSQUE_TYPES[mosque.type].color }}
              >
                {MOSQUE_TYPES[mosque.type].label}
              </div>
            </div>
            <button className="close-btn" onClick={handleClose} data-testid="mosque-details-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="details-content">
              <div className="detail-section">
                <h3>{t('mosqueDetails.access')}</h3>
                <div className="access-status">
                  {mosque.isPublic ? (
                    <span className="public-badge">
                      <Globe size={16} /> {t('mosqueDetails.public')}
                    </span>
                  ) : (
                    <span className="private-badge">
                      <Lock size={16} /> {t('mosqueDetails.private')}
                    </span>
                  )}
                </div>
              </div>

              {mosque.description && (
                <div className="detail-section">
                  <h3>{t('mosqueDetails.description')}</h3>
                  <p className="description-text">{mosque.description}</p>
                </div>
              )}

              {normalizedSourceLink && (
                <div className="detail-section">
                  <h3>{t('mosqueDetails.source')}</h3>
                  <a href={normalizedSourceLink} target="_blank" rel="noopener noreferrer">
                    {normalizedSourceLink}
                  </a>
                </div>
              )}

              <div className="detail-section">
                <h3>{t('mosqueDetails.facilities')}</h3>
                <div className="facilities-list">
                  {Object.entries(mosque.facilities).map(([key, available]) => {
                    const status = getFacilityStatus(available);
                    return (
                      <div key={key} className={`facility-item ${status.className}`}>
                        <span className="facility-label">
                          {t(`facilities.${key}`)}
                        </span>
                        <span className="facility-status">
                          {status.icon}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="detail-section">
                <h3>{t('mosqueDetails.location')}</h3>
                <button
                  type="button"
                  className="coordinates"
                  onClick={() => void handleCoordinatesClick()}
                  title={t('mosqueDetails.copyCoordinates')}
                  aria-label={t('mosqueDetails.copyCoordinates')}
                >
                  <span className="coordinates-value">
                    <MapPin size={14} /> {mosque.location.lat.toFixed(6)}, {mosque.location.lng.toFixed(6)}
                  </span>
                  <span className="coordinates-copy-icon">
                    <Copy size={14} />
                  </span>
                </button>
                {mosque.location.address && (
                  <button
                    type="button"
                    className="address-copy"
                    onClick={() => void handleAddressClick()}
                    title={t('mosqueDetails.copyAddress')}
                    aria-label={t('mosqueDetails.copyAddress')}
                  >
                    <span className="address-value">{mosque.location.address}</span>
                    <span className="coordinates-copy-icon">
                      <Copy size={14} />
                    </span>
                  </button>
                )}
                <button type="button" className="share-inline-text" onClick={() => void handleShare()}>
                  <Share2 size={13} />
                  {t('mosqueDetails.share')}
                </button>
              </div>

            <div className="detail-section">
              <h3>{t('photos.detailsTitle')}</h3>
              <PhotoGallery photos={photos} loading={isLoadingPhotos} />
              <button
                className="btn-edit btn-add-photo-inline"
                onClick={() => {
                  trackEvent('add_photo_opened', { mosque_type: mosque.type });
                  setIsAddPhotoOpen(true);
                }}
              >
                <Images size={16} /> {t('photos.addPhotos')}
              </button>
            </div>

              <div className="detail-section">
                <h3>{t('mosqueDetails.information')}</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">{t('mosqueDetails.addedBy')}</span>
                    <span className="info-value">{mosque.createdBy}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('mosqueDetails.created')}</span>
                    <span className="info-value">
                      {new Date(mosque.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('mosqueDetails.updated')}</span>
                    <span className="info-value">
                      {new Date(mosque.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

        </div>

        <div className="details-actions">
          <div className="details-actions-row details-actions-row-dual">
            <button
              className="btn-edit"
              onClick={() => {
                trackEvent('suggest_edit_clicked', { mosque_type: mosque.type });
                onSuggestEdit?.(mosque);
              }}
            >
              <Pencil size={16} /> {t('mosqueDetails.suggestEdit')}
            </button>
            <button className="btn-directions" onClick={handleGetDirections}>
              <Navigation size={16} /> {t('mosqueDetails.getDirections')}
            </button>
          </div>
        </div>
      </div>

      <AddPhotoForm
        isOpen={isAddPhotoOpen}
        mosque={mosque}
        onClose={() => setIsAddPhotoOpen(false)}
        onUploaded={loadPhotos}
      />
    </div>
  );
};

export default MosqueDetails;
